import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaArrowLeft, FaArrowRight, FaCheck } from "react-icons/fa";
import {
  getTeamImages,
  createGame,
  updateGame,
  getGameForEdit,
  getMyGames,
} from "../../api/game";
import ModePicker from "../../components/game/ModePicker";
import TeamsEditor from "../../components/game/TeamsEditor";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { Button, Field, Input, PageSpinner } from "../../ui";

const emptyGameInfo = (firstTeamImageId) => ({
  gameName: "لعبة جديدة",
  modes: [],
  customModes: [],
  teams: [
    { name: "", image: firstTeamImageId },
    { name: "", image: firstTeamImageId },
  ],
  playedQuestions: false,
  fadedQuestions: false,
  lowToHighPoints: false,
  from246PointsSystem: false,
  from135PointsSystem: false,
});

const GameCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const queryClient = useQueryClient();
  const { isPremium, isLimitReached, showUpgradeModal } = useSubscription();

  const [step, setStep] = useState(1);
  const [gameInfo, setGameInfo] = useState(null);

  const { data: teamImages } = useQuery({
    queryKey: ["teamImages"],
    queryFn: getTeamImages,
  });
  const { data: editGame } = useQuery({
    queryKey: ["gameEdit", id],
    queryFn: () => getGameForEdit(id),
    enabled: isEdit,
  });
  const { data: myGames } = useQuery({
    queryKey: ["myGames"],
    queryFn: getMyGames,
  });

  // Initialise gameInfo once data is ready.
  useEffect(() => {
    if (gameInfo) return;
    if (isEdit) {
      if (!editGame) return;
      setGameInfo({
        gameName: editGame.name || "",
        modes: (editGame.modes || []).map((m) => m._id),
        customModes: (editGame.customModes || []).map((m) => m._id),
        teams: (editGame.teams || []).map((t) => ({
          name: t.name || "",
          image: t.image?._id || t.image,
        })),
        playedQuestions: !!editGame.playedQuestions,
        fadedQuestions: !!editGame.fadedQuestions,
        lowToHighPoints: !!editGame.lowToHighPoints,
        from246PointsSystem: !!editGame.from246PointsSystem,
        from135PointsSystem: !!editGame.from135PointsSystem,
      });
    } else if (teamImages) {
      setGameInfo(emptyGameInfo(teamImages?.[0]?._id));
    }
  }, [isEdit, editGame, teamImages, gameInfo]);

  // Guard: free users at the games cap can't reach the creator (create only).
  useEffect(() => {
    if (!isEdit && myGames && isLimitReached("MAX_GAMES", myGames.length)) {
      queueEvent(EVENTS.GAME_CAP_REACHED, {
        count: myGames.length,
        source: "game_create_guard",
      });
      showUpgradeModal({ messageKey: "GAMES_LIMIT" });
      navigate("/app/games", { replace: true });
    }
  }, [isEdit, myGames, isLimitReached, showUpgradeModal, navigate]);

  const { mutate: submit, isPending } = useMutation({
    mutationFn: () => {
      const payload = {
        gameName: gameInfo.gameName,
        modes: gameInfo.modes,
        customModes: gameInfo.customModes,
        teams: gameInfo.teams.map((t, i) => ({
          name: t.name?.trim() ? t.name.trim() : `الفريق ${i + 1}`,
          image: t.image,
        })),
        playedQuestions: gameInfo.playedQuestions,
        fadedQuestions: gameInfo.fadedQuestions,
        lowToHighPoints: gameInfo.lowToHighPoints,
        from246PointsSystem: gameInfo.from246PointsSystem,
        from135PointsSystem: gameInfo.from135PointsSystem,
      };
      return isEdit ? updateGame(id, payload) : createGame(payload);
    },
    onSuccess: (game) => {
      queryClient.invalidateQueries({ queryKey: ["myGames"] });
      toast.success(isEdit ? "تم حفظ التغييرات" : "تم إنشاء اللعبة");
      if (isEdit) navigate("/app/games");
      else navigate(`/app/games/${game?._id || ""}`);
    },
    onError: (error) => {
      const msg = error?.response?.data?.message;
      const code = error?.response?.data?.code;
      if (code === "PREMIUM_MODE_REQUIRED") {
        showUpgradeModal({ messageKey: "PREMIUM_MODE" });
      } else if (code === "PREMIUM_REQUIRED") {
        showUpgradeModal({ messageKey: "GAMES_LIMIT" });
      } else if (code === "SINGLE_MODE_INSUFFICIENT_QUESTIONS") {
        toast.error("هذه الفئة لا تحتوي على أسئلة كافية للعب فئة واحدة");
      } else {
        toast.error(msg || "تعذّر حفظ اللعبة");
      }
    },
  });

  const goToStep2 = () => {
    const total =
      (gameInfo.modes?.length || 0) + (gameInfo.customModes?.length || 0);
    const validCount = total === 1 || (total >= 3 && total <= 6);
    if (!gameInfo.gameName?.trim()) {
      toast.error("الرجاء إدخال عنوان للعبة");
      return;
    }
    if (!validCount) {
      toast.error("اختر فئة واحدة، أو من 3 إلى 6 فئات");
      return;
    }
    if (total === 1 && !isPremium) {
      showUpgradeModal({ messageKey: "GENERIC" });
      return;
    }
    setStep(2);
  };

  if (!gameInfo) {
    return <PageSpinner />;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <button
          type="button"
          onClick={() => (step === 2 ? setStep(1) : navigate("/app/games"))}
          className="shrink-0 w-10 h-10 rounded-xl border border-white/10 bg-white/[0.05] text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center"
          aria-label="رجوع"
        >
          <FaArrowRight aria-hidden="true" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-extrabold">
            {step === 1 ? "مصنع الألعاب" : "تضبيط الفِرَق"}
          </h1>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex gap-1.5" aria-hidden="true">
              {[1, 2].map((n) => (
                <span
                  key={n}
                  className={`h-1.5 w-8 rounded-full ${
                    n <= step ? "bg-primary" : "bg-white/15"
                  }`}
                />
              ))}
            </div>
            <span className="text-white/55 text-sm">الخطوة {step} من 2</span>
          </div>
        </div>
      </div>

      {step === 1 ? (
        <div>
          <Field label="العنوان" htmlFor="game-name" className="mb-6">
            <Input
              id="game-name"
              type="text"
              value={gameInfo.gameName}
              onChange={(e) =>
                setGameInfo((prev) => ({ ...prev, gameName: e.target.value }))
              }
              placeholder="عنوان اللعبة"
            />
          </Field>

          <div className="mb-3">
            <h2 className="text-lg font-bold">اختر الفئات</h2>
            <p className="text-white/55 text-sm">
              اختر فئة واحدة، أو من 3 إلى 6 فئات
            </p>
          </div>
          <ModePicker gameInfo={gameInfo} setGameInfo={setGameInfo} />

          <Button
            block
            size="lg"
            className="mt-8"
            onClick={goToStep2}
            iconEnd={<FaArrowLeft className="text-base" aria-hidden="true" />}
          >
            التالي
          </Button>
        </div>
      ) : (
        <div>
          <TeamsEditor
            gameInfo={gameInfo}
            setGameInfo={setGameInfo}
            teamImages={teamImages}
          />
          <Button
            block
            size="lg"
            className="mt-8"
            onClick={() => submit()}
            loading={isPending}
            icon={<FaCheck className="text-base" aria-hidden="true" />}
          >
            {isEdit ? "حفظ التغييرات" : "إنشاء اللعبة"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GameCreate;
